import { useEffect } from "react";
import { Hub } from "@aws-amplify/core";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { Auth, CognitoHostedUIIdentityProvider } from "@aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import PositionedSpinner from "../components/Spinner";

const RequireAuth = ({ children }: { children: JSX.Element }) => {
  const navigate = useNavigate();

  // The context callback is meant to help prevent unnecessary rerenders
  const { route } = useAuthenticator((context) => [context.route]);

  // Effect hook which will redirect to the original route after the user has authenticated
  // Without this hook, all users will be redirected to the default home page after login
  useEffect(() => {
    const hubListenerCancelToken = Hub.listen(
      "auth",
      ({ payload: { event, data } }) => {
        if (event === "customOAuthState") {
          navigate(data as string, { replace: true });
        }
      }
    );

    // Always return a cleanup function to prevent memory leaks
    // In this case, Hub.listen returns the function to unsubscribe from the event
    return hubListenerCancelToken;
  }, []);

  // Effect hook which handles the authentication flow
  useEffect(() => {
    const authenticate = async () => {
      if (route === "setup") {
        // Use this to show the Cognito Hosted UI
        await Auth.federatedSignIn({
          provider: CognitoHostedUIIdentityProvider.Cognito,
          customState: window.location.pathname,
        });
      }
    };

    authenticate().catch((error) => {
      let message = `Error authenticating the user`;
      if (error instanceof Error) {
        message += `: ${error.message}`;
      }
      throw new Error(message);
    });
  }, [route]);

  if (route === "idle" || route === "setup") {
    return <PositionedSpinner />;
  }
  return children;
};

export default RequireAuth;
